<template>
  <div>
    <Loading v-if="isLoading" />
    <template v-else>
      <ValidationObserver v-slot="{ handleSubmit, valid }">
        <form @submit.prevent="handleSubmit(onSubmit)">
          <div class="p-box">
            <div class="p-box__body">
              <div class="p-box">
                <div class="p-box__header">
                  <p class="p-box__title">
                    {{ $t('pages.companies.searchPatternForm.titleA') }}
                  </p>
                </div>
                <div class="p-box__body">
                  <div class="formContainer">
                    <ValidationProvider v-slot="{ errors, value, handleChange }" v-model="name" :name="$t('models.notificationConfig.name')"
                                        rules="required|max:40"
                    >
                      <label class="c-formLabel">
                        {{
                          $t('pages.companies.searchPatternForm.label.name', {count: name.length, max: 40})
                        }}{{ $t('labels.requiredLabel') }}
                      </label>
                      <span v-for="error in errors" :key="error" class="c-error-msg">
                        {{ error }}
                      </span>
                      <TextField :model-value="value" class="c-textField"
                                 :placeholder="$t('labels.placeholder.enter', { value: $t('models.notificationConfig.name') })"
                                 @update:model-value="handleChange"
                      />
                    </ValidationProvider>
                  </div>
                  <div class="formContainer">
                    <label class="c-formLabel">{{
                      $t('pages.companies.searchPatternForm.label.notificationConfig')
                    }}</label>
                    <div class="notificationContainer">
                      <template v-for="(key, value) in emailNotificationConfigs" :key="key">
                        <div class="c-radio__item">
                          <input
                            :id="value"
                            v-model="notificationConfig"
                            type="radio"
                            name="status_id"
                            class="c-radio__input"
                            :value="value"
                          >
                          <label :for="value" class="c-radio__label">
                            {{ key }}
                          </label>
                        </div>
                      </template>
                    </div>
                    <div v-if="notificationConfig === 'weekly'" class="p-registration__body__select">
                      <Select
                        v-model="wday"
                        :options="wdayOptions"
                      />
                    </div>
                    <div v-if="notificationConfig === 'monthly'" class="p-registration__body__select">
                      <Select
                        v-model="day"
                        :options="dayOptions"
                      />
                    </div>
                  </div>
                  <div class="formContainer">
                    <FilterCondition
                      v-if="Object.keys(searchPattern.condition).some(key => searchPattern.condition[key] && searchPattern.condition[key].length !== 0)"
                      :search-attributes="searchAttributes"
                      :form-values="fromSearchPattern"
                      :disable-filter-item-types="Object.keys(searchAttributes).filter((key) => !Object.keys(fromSearchPattern).includes(key))"
                      :display-first-single-line-types="['comments', 'replies', 'ratings']"
                      :can-save="false"
                    />
                    <div class="info">
                      <Info>
                        <p>{{ $t('pages.companies.searchPatternForm.info') }}</p>
                      </Info>
                    </div>
                  </div>
                </div>
              </div>
              <div class="p-box">
                <div class="p-box__header">
                  <p class="p-box__title">
                    {{ $t('pages.companies.searchPatternForm.titleB') }}
                  </p>
                </div>
                <div class="p-box__body">
                  <div class="formContainer">
                    <label class="c-formLabel">{{
                      $t('pages.companies.searchPatternForm.label.users', {
                        count: selectedUserCompanies.length,
                        max: 40
                      })
                    }}{{ $t('labels.requiredLabel') }}</label>
                    <SuggestField
                      :value="suggestValue"
                      :placeholder="$t('labels.placeholder.addMember')"
                      :suggests="isPossibleSelectUsers"
                      @select-suggest="selectSuggest"
                      @close-suggest="() => {}"
                      @focus-field="() => {}"
                      @input-field="inputField"
                    />
                  </div>
                  <div class="c-button c-button--icon">
                    <label class="c-button__item c-button__item--primary" @click="addAllUserCompanies">
                      <Icon name="iconPlusRed" class="c-button__icon" />
                      {{ $t('components.SettingEmailNotificationModal.addAllUsers') }}
                    </label>
                  </div>
                  <div>
                    <template v-for="user in selectedUserCompanies" :key="user.id">
                      <div class="users">
                        <div class="users__container">{{ user.name }}</div>
                        <div class="users__container--email">{{ user.email }}</div>
                        <div class="users__container">
                          {{ $t(`labels.roles.${user.role}`) }}({{
                            storeCountLabel(user)
                          }})
                        </div>
                        <div class="users__container--button" @click="removeUser(user.id)">
                          <Icon name="iconCross" />
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
            <div class="p-modal__content__footer">
              <div class="c-button c-button--right">
                <router-link class="c-button__item c-button__item--outline-cancel"
                             :to="{ name: 'CompaniesManagementSearchPatterns', params: { company_uuid: currentCompanyUuid } }"
                >
                  {{ $t('labels.cancel') }}
                </router-link>
                <button class="c-button__item c-button__item--primary"
                        :class="{'c-button__item--disabled': (!valid || !validate)}" :disabled="!valid || !validate"
                >
                  {{ $t('labels.save') }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </template>
  </div>
</template>

<script>
import Loading from '../components/Loading'
import Select from '../components/Select'
import TextField from '../components/TextField'
import SuggestField from '../components/SuggestField'
import Icon from '../components/Icon'
import {emailNotificationConfigs} from '../modules/emailNotificationConfigConst'
import {dayOfWeek} from '../modules/dayOfWeekConst'
import {mapState, mapActions} from 'vuex'
import FilterCondition from '../components/FilterCondition'
import Info from '../components/Info'

export default {
  name: 'CompaniesSearchPatternForm',
  components: {
    Loading,
    TextField,
    Select,
    SuggestField,
    Icon,
    FilterCondition,
    Info
  },
  emits: [
    'update-title',
    'update-breadcrumbs'
  ],
  data() {
    return {
      name: '',
      notificationConfig: 'daily',
      wday: 'sunday',
      day: 1,
      suggestValue: '',
      userCompanyIds: [],
      title: '',
      loading: false,
    }
  },
  computed: {
    ...mapState({
      userCompanies: state => state.companiesUserCompany.data,
      userCompaniesLoading: state => state.companiesUserCompany.loading,
      searchPattern: state => state.companiesSearchPatterns.data,
      searchPatternLoading: state => state.companiesSearchPatterns.loading,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
    }),
    isLoading() {
      return this.loading || this.searchAttributesLoading || this.userCompaniesLoading || this.searchPatternLoading
    },
    currentCompanyUuid() {
      return this.$route.params.company_uuid
    },
    searchPatternId() {
      return this.$route.params.search_pattern_id
    },
    emailNotificationConfigs: () => emailNotificationConfigs,
    dayOfWeek: () => dayOfWeek,
    dayOptions() {
      const MAX_DAY = 31
      const results = {}
      const array = [...Array(MAX_DAY)]
      array.forEach((_, i) => results[`${i + 1}日`] = i+1)
      return results
    },
    selectedUserCompanies() {
      return this.userCompanies.filter(user => this.userCompanyIds.includes(user.id))
    },
    wdayOptions() {
      const results = {}
      Object.keys(this.dayOfWeek).forEach(key => results[this.dayOfWeek[key]] = key)
      return results
    },
    isPossibleSelectUsers() {
      if (!this.userCompanies) {
        return []
      }
      const filterUsers = this.userCompanies.filter(user =>
        user.email.includes(this.suggestValue) || user.name.includes(this.suggestValue) || this.$t(`labels.roles.${user.role}`).includes(this.suggestValue)
      )
      return filterUsers.map(user => (
        {
          'id': user.id,
          'name': `
                    <div class="suggest">
                      <div class="suggest__value">${user.name}</div>
                      <div class="suggest__emailValue">${user.email}</div>
                      <div class="suggest__value">${this.$t(`labels.roles.${user.role}`)}(${this.storeCountLabel(user)})</div>
                    </div>
                  `
        }
      ))
    },
    fromSearchPattern() {
      const condition = this.searchPattern.condition
      return {
        stores: condition.storeUuids,
        sites: condition.siteTypes,
        brands: condition.brandIds,
        franchisees: condition.franchiseeIds,
        areas: condition.areaIds,
        user_companies: condition.userCompanyIds,
        prefectures: condition.prefectures,
        ratings: condition.ratings,
        comments: condition.comments,
        replies: condition.replies,
        locales: condition.locales,
        original_tag_group_a: condition.originalTagGroupATagIds,
        original_tag_group_b: condition.originalTagGroupBTagIds,
        original_tag_group_c: condition.originalTagGroupCTagIds,
        original_tag_group_d: condition.originalTagGroupDTagIds,
        original_tag_group_e: condition.originalTagGroupETagIds,
        query: condition.query
      }
    },
    validate() {
      let valid = true

      if(this.notificationConfig === 'weekly' && !Object.keys(dayOfWeek).includes(this.wday)) valid = false
      if(this.notificationConfig === 'monthly' && (this.day === undefined || this.day === null || Number(this.day) < 0 || Number(this.day) > 30)) valid = false //dayは0はじまり
      if(this.selectedUserCompanies.length === 0) valid = false

      return valid
    },
  },
  async created() {
    await Promise.all([
      this.readUserCompanies(),
      this.readSearchPattern(this.searchPatternId),
      this.readSearchAttributes()
    ])

    this.name = this.searchPattern.name
    this.notificationConfig = this.getReceiveFrequency(this.searchPattern.notificationConfig)
    this.wday = this.searchPattern.notificationConfig.wday
    this.day = this.searchPattern.notificationConfig.day
    this.userCompanyIds = this.searchPattern.notificationConfig.userCompanyIds

    this.$emit('update-title', this.$t('pages.companies.management.searchPatterns.editTitle', {name: this.searchPattern.name}))

    const breadcrumbs = [
      {
        name: this.$t('pages.CompaniesDashboards.title'),
        pathName: 'CompaniesDashboards',
        query: {},
      },
      {
        name: this.$t('pages.companies.management.title'),
        pathName: 'CompaniesManagement',
        query: {},
      },
      {
        name: this.$t('pages.companies.management.searchPatterns.title'),
        pathName: 'CompaniesManagementSearchPatterns',
        query: {},
      },
      {
        name: this.$t('pages.companies.management.searchPatterns.editTitle', {name: this.searchPattern.name})
      },
    ]
    this.$emit('update-breadcrumbs', breadcrumbs)
  },
  methods: {
    ...mapActions({
      readUserCompanies: 'companiesUserCompany/readUserCompanies',
      readSearchPattern: 'companiesSearchPatterns/readSearchPattern',
      updateSearchPattern: 'companiesSearchPatterns/updateSearchPattern',
      createsNotificationConfigs: 'companiesSearchPatternsNotificationConfigs/createsNotificationConfigs',
      readSearchAttributes: 'companiesSearchTagGroups/readSearchTagGroups',
    }),
    selectSuggest({suggest}) {
      this.addUserCompanies(suggest.id)
      this.suggestValue = ''
    },
    inputField({value}) {
      this.suggestValue = value
    },
    addUserCompanies(value) {
      this.userCompanyIds.push(value)
    },
    addAllUserCompanies() {
      this.userCompanyIds = this.userCompanies.map((user) => user.id)
    },
    removeUser(removeId) {
      this.userCompanyIds = this.userCompanyIds.filter(id => removeId !== id)
    },
    getReceiveFrequency(notificationConfig) {
      if (notificationConfig.wday) return 'weekly'
      if (notificationConfig.day) return 'monthly'
      return 'daily'
    },
    async onSubmit() {
      const searchPatternPayload = {
        search_pattern: {
          name: this.name,
          kind: this.searchPattern.kind,
        }
      }

      const notificationConfigPayload = {
        notification_config: {
          kind: this.notificationConfig,
          user_company_ids: this.userCompanyIds
        }
      }
      if(this.notificationConfig === 'weekly') notificationConfigPayload['notification_config']['wday'] = this.wday
      if(this.notificationConfig === 'monthly') notificationConfigPayload['notification_config']['day'] = this.day

      let isSuccess

      this.loading = true
      if(this.searchPatternId){
        isSuccess = await this.updateSearchPattern({ searchPatternId: this.searchPatternId, payload: searchPatternPayload })
      }

      if(isSuccess) {
        isSuccess = await this.createsNotificationConfigs({ searchPatternId: this.searchPatternId,  payload: notificationConfigPayload })
      }

      this.loading = false

      if(isSuccess) {
        this.$router.push({ name: 'CompaniesManagementSearchPatterns', params: { company_uuid: this.currentCompanyUuid } })
      }
    },
    storeCountLabel(user) {
      return user.readableAllStore ? this.$t('labels.allStores') : this.$t('labels.simpleStoresCount', { count: user.stores.length } )
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../../stylesheets/biz_spa/foundation/_var.scss';

.formContainer {
  margin: 0 0 30px 0;
}

.notificationContainer {
  display: flex;
}

.info {
  margin-top: 16px;
}

.users {
  display: flex;
  padding: 12px;
  background: #ECEDF0;
  margin-top: 12px;

  &__container {
    width: 25%;
    margin-right: 5px;
    white-space: pre-wrap;
    overflow-wrap: break-word;

    &--email {
      width: 50%;
      margin-right: 5px;
      white-space: pre-wrap;
      overflow-wrap: break-word;
    }

    &--button {
      width: 10%;
      display: flex;
      align-items: center;
      cursor: pointer;
    }
  }

}

:deep(.suggest) {
  display: flex;
}

:deep(.suggest__value) {
  width: 25%;
  margin-right: 5px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

:deep(.suggest__emailValue) {
  width: 50%;
  margin-right: 5px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}
</style>